/**
 * Linked actions: activating one hotspot or 3D model (the source) also drives a DIFFERENT
 * hotspot or model (the target). The classic case is a "video sign with a PNG label": an image
 * hotspot under a video hotspot whose click starts the video.
 *
 * This module holds the action logic. createViewer implements `LinkedActionResolver` over its
 * runtime entities (hotspot video elements, audio slots, model anim components), so the logic
 * here stays free of PlayCanvas and unit-testable.
 *
 * Semantics (mirrored in the Hotspots / 3D Models docs and storysplat-viewer/README.md):
 * - When: a source runs its actions when it is activated, in addition to its own behaviour
 *   (popup, teleport, its own media). Hotspots: on click for 'click' / 'none' activation, on
 *   pointer-enter for 'hover' activation; `viewer.triggerHotspot(id)` runs them too. Models: on
 *   click, or on hover-in when `interaction.activationMode` is 'hover'. The editor viewport runs
 *   them exactly like a published scene, because it runs the source's own click the same way.
 * - No cascade: an action never runs the target's own linked actions or emits events for it,
 *   so two hotspots that point at each other cannot loop. A target equal to the source is
 *   ignored.
 * - Lookup by id each time: a deleted hotspot or model, or one that is still loading, is skipped
 *   and logged once per action. So is an action the target can't perform (playVideo on a hotspot
 *   with no video, playAnimation on a model with no animations).
 * - Video targets resolve through the target entity's `videoElement`, so any marker that carries
 *   one (a video hotspot, a stacked marker's video layer) works. Play starts the video through
 *   the target's own play path (its videoMuted setting, alpha mask, spatial audio). A play from a
 *   click runs inside that user gesture, so sound is allowed; if the browser still refuses
 *   (NotAllowedError, e.g. a hover before the visitor clicked anything) it retries muted and logs
 *   it. playVideo on an autoplay-mode video that is already playing muted unmutes it, as
 *   clicking that video does (only once the page has had a user gesture, else it stays muted).
 * - Trigger hold: a linked play or pause overrides the target's automatic trigger (proximity,
 *   scroll, autoplay) for as long as that trigger's condition stays as it was when the action
 *   ran, and hands control back the moment the condition flips. A proximity video played from
 *   outside its range keeps playing until the visitor walks in and back out (then
 *   pause-on-leave applies as usual); an autoplay or scroll video paused by a link stays paused
 *   until it leaves and re-enters its visible range. Click-mode videos have no automatic
 *   trigger, so nothing is held. Leaving a waypoint and closing a popup still pause non-autoplay
 *   videos, as they always have.
 * - Audio follows the target's click toggle: toggleAudio pauses ('pause-resume') or stops
 *   ('stop-restart') a playing clip; playAudio resumes a paused clip under 'pause-resume'.
 * - Animation plays exactly like the model's own animation trigger (same clips, same looping);
 *   stopAnimation pauses where it is and the next play resumes.
 * - Keyframe animations (target kind 'animation'): play resumes a paused animation and starts a
 *   finished 'once' animation over; pause holds the current pose; toggle flips between them;
 *   restart starts over from the beginning, honoring its delay. Scroll-linked animations follow
 *   the tour and can't be played, so their actions are skipped (logged once).
 * - openPopup shows the target's popup (AR content in XR) as its click would, even when the
 *   target's own activation is 'none'. One popup is open at a time: when the clicked source
 *   has popup content of its own, its popup is the one that stays open.
 */
import type {
    LinkedAction,
    LinkedActionTargetKind,
    LinkedActionType,
    LinkedEntityAnimationActionType,
    LinkedHotspotActionType,
    LinkedModelActionType,
} from '../types';

export const LINKED_VIDEO_ACTIONS: readonly LinkedHotspotActionType[] = ['playVideo', 'pauseVideo', 'toggleVideo', 'restartVideo'];
export const LINKED_AUDIO_ACTIONS: readonly LinkedHotspotActionType[] = ['playAudio', 'stopAudio', 'toggleAudio'];
export const LINKED_POPUP_ACTIONS: readonly LinkedHotspotActionType[] = ['openPopup'];
export const LINKED_HOTSPOT_ACTIONS: readonly LinkedHotspotActionType[] = [...LINKED_VIDEO_ACTIONS, ...LINKED_POPUP_ACTIONS, ...LINKED_AUDIO_ACTIONS];
export const LINKED_MODEL_ACTIONS: readonly LinkedModelActionType[] = ['playAnimation', 'stopAnimation', 'toggleAnimation'];
export const LINKED_ENTITY_ANIMATION_ACTIONS: readonly LinkedEntityAnimationActionType[] = ['playEntityAnimation', 'pauseEntityAnimation', 'toggleEntityAnimation', 'restartEntityAnimation'];

const HOTSPOT_ACTION_SET: ReadonlySet<string> = new Set(LINKED_HOTSPOT_ACTIONS);
const MODEL_ACTION_SET: ReadonlySet<string> = new Set(LINKED_MODEL_ACTIONS);
const ENTITY_ANIMATION_ACTION_SET: ReadonlySet<string> = new Set(LINKED_ENTITY_ANIMATION_ACTIONS);

/** Whether `action` is one a target of `kind` can run (video/popup/audio for hotspots, GLB clips for models, keyframe playback for animations). */
export function isLinkedActionForKind(kind: LinkedActionTargetKind, action: string): action is LinkedActionType {
    switch (kind) {
        case 'hotspot': return HOTSPOT_ACTION_SET.has(action);
        case 'model': return MODEL_ACTION_SET.has(action);
        case 'animation': return ENTITY_ANIMATION_ACTION_SET.has(action);
        default: return false;
    }
}

/** The loosely-typed shape scene JSON may hold; validated by normalizeLinkedAction. */
export type LinkedActionInput = {
    id?: string;
    target?: { kind?: string; id?: string } | null;
    action?: string;
} | null | undefined;

/** A well-formed copy of `input`, or null when it can't run (bad target or an action the target kind lacks). */
export function normalizeLinkedAction(input: LinkedActionInput): LinkedAction | null {
    if (!input || typeof input !== 'object') return null;
    const target = input.target;
    if (!target || typeof target !== 'object') return null;
    const kind = target.kind;
    if (kind !== 'hotspot' && kind !== 'model' && kind !== 'animation') return null;
    if (typeof target.id !== 'string' || target.id === '') return null;
    if (typeof input.action !== 'string' || !isLinkedActionForKind(kind, input.action)) return null;
    return { id: typeof input.id === 'string' ? input.id : '', target: { kind, id: target.id }, action: input.action };
}

/** True when any of `actions` opens a popup (a content-less source must not close it right after). */
export function linkedActionsOpenPopup(actions: readonly LinkedActionInput[] | null | undefined): boolean {
    return Array.isArray(actions) && actions.some((a) => normalizeLinkedAction(a)?.action === 'openPopup');
}

// ── Resolver interface (implemented by createViewer) ─────────────────────────────────────

/** The subset of HTMLVideoElement the actions touch. */
export interface LinkedVideoElementLike {
    paused: boolean;
    muted: boolean;
    play(): Promise<void> | void;
}

export interface LinkedVideoControl {
    /** The target entity's video element (a video hotspot, or a stacked marker's video layer). */
    readonly element: LinkedVideoElementLike;
    /** The target's own click unmutes it while it plays (autoplay mode); playVideo does the same. */
    readonly unmuteWhenPlaying: boolean;
    /** Start through the target's own play path; returns the element's play() promise. */
    play(): Promise<void> | void;
    /** Pause through the target's own pause path. */
    pause(): void;
    /** Seek the video (and any mask video) back to the start. */
    rewind(): void;
    /** Hold the target's automatic trigger at its current state (see holdAutoTrigger). */
    holdTrigger(): void;
}

export type LinkedAudioState = 'playing' | 'paused' | 'stopped';

export interface LinkedAudioControl {
    /** How the target's own click toggles its audio. */
    readonly clickBehavior: 'pause-resume' | 'stop-restart';
    /** False while the audio asset is still loading. */
    ready(): boolean;
    state(): LinkedAudioState;
    play(): void;
    resume(): void;
    pause(): void;
    stop(): void;
}

export interface LinkedHotspotTarget {
    readonly video: LinkedVideoControl | null;
    readonly audio: LinkedAudioControl | null;
    /** Show the popup as the target's own click would; false when it has no popup content. */
    openPopup(): boolean;
}

export interface LinkedAnimationControl {
    isPlaying(): boolean;
    play(): void;
    stop(): void;
}

export interface LinkedModelTarget {
    /** null when the model has no animations (or hasn't finished loading). */
    readonly animation: LinkedAnimationControl | null;
}

/** A keyframe animation (EntityAnimationSystem) as a linked-action target. */
export interface LinkedEntityAnimationTarget {
    /** False for scroll-linked animations, which follow the tour and can't be played. */
    readonly timeBased: boolean;
    isPlaying(): boolean;
    play(): void;
    pause(): void;
    restart(): void;
}

export interface LinkedActionResolver {
    getHotspot(id: string): LinkedHotspotTarget | null;
    getModel(id: string): LinkedModelTarget | null;
    /** Optional: resolvers without keyframe animations skip 'animation' targets. */
    getAnimation?(id: string): LinkedEntityAnimationTarget | null;
}

export interface LinkedActionLogger {
    log(message: string): void;
    warn(message: string): void;
}

export interface LinkedActionSource {
    kind: LinkedActionTargetKind;
    id: string;
}

export interface LinkedActionRunner {
    /**
     * Run a source's actions in order. Every action STARTS synchronously, so a video play still
     * happens inside the caller's click gesture; the promise settles when all of them have.
     */
    run(actions: readonly LinkedActionInput[] | null | undefined, source?: LinkedActionSource): Promise<void>;
    /** Run one action (the public `viewer.runLinkedAction`). */
    runAction(action: LinkedActionInput, source?: LinkedActionSource): Promise<void>;
}

const PREFIX = '[LinkedActions]';
/** Sticky user activation (true where the browser can't tell, e.g. outside a browser). */
const pageHasBeenActivated = (): boolean =>
    typeof navigator === 'undefined' || !navigator.userActivation || navigator.userActivation.hasBeenActive;
const DONE: Promise<void> = Promise.resolve();

type ErrorLike = { name?: string; message?: string } | string | null | undefined;
const errorName = (err: ErrorLike): string => (err && typeof err === 'object' ? err.name || err.message || 'Error' : String(err));
const errorText = (err: ErrorLike): string => (err && typeof err === 'object' ? err.message || err.name || 'Error' : String(err));

export function createLinkedActionRunner(resolver: LinkedActionResolver, logger: LinkedActionLogger = console): LinkedActionRunner {
    // "Log once" bookkeeping: a missing target is reported the first time, not on every click.
    const reported = new Set<string>();
    const skipOnce = (key: string, message: string): Promise<void> => {
        if (!reported.has(key)) {
            reported.add(key);
            logger.warn(`${PREFIX} ${message}`);
        }
        return DONE;
    };

    const retryMuted = (video: LinkedVideoControl, label: string, err: ErrorLike): Promise<void> => {
        const el = video.element;
        const name = errorName(err);
        // AbortError & co. mean something else (a pause, a new source) won the race: leave it be.
        if (name !== 'NotAllowedError' || el.muted) {
            logger.warn(`${PREFIX} ${label}: the video did not start (${name}).`);
            return DONE;
        }
        logger.warn(`${PREFIX} ${label}: the browser blocked playback with sound; playing muted instead.`);
        el.muted = true;
        let retry: Promise<void> | void;
        try {
            retry = el.play();
        } catch (e) {
            retry = Promise.reject(e);
        }
        return Promise.resolve(retry).then(
            () => undefined,
            (e) => { logger.warn(`${PREFIX} ${label}: the video did not start even muted (${errorName(e)}).`); },
        );
    };

    const startVideo = (video: LinkedVideoControl, label: string): Promise<void> => {
        const el = video.element;
        if (!el.paused) {
            // Unmuting without any user activation would make the browser pause it instead.
            if (video.unmuteWhenPlaying && el.muted && pageHasBeenActivated()) {
                el.muted = false;
                logger.log(`${PREFIX} ${label}: unmuted the playing video.`);
            }
            return DONE;
        }
        video.holdTrigger();
        let started: Promise<void> | void;
        try {
            started = video.play();
        } catch (e) {
            started = Promise.reject(e);
        }
        if (!started) return DONE;
        return started.then(() => undefined, (err) => retryMuted(video, label, err));
    };

    const runHotspotAction = (type: LinkedHotspotActionType, target: LinkedHotspotTarget, key: string, label: string): Promise<void> => {
        switch (type) {
            case 'playVideo':
            case 'pauseVideo':
            case 'toggleVideo':
            case 'restartVideo': {
                const video = target.video;
                if (!video) return skipOnce(`novideo:${key}`, `${label}: the target hotspot has no video; skipped.`);
                if (type === 'pauseVideo' || (type === 'toggleVideo' && !video.element.paused)) {
                    if (!video.element.paused) {
                        video.holdTrigger();
                        video.pause();
                    }
                    return DONE;
                }
                if (type === 'restartVideo') video.rewind();
                return startVideo(video, label);
            }
            case 'openPopup':
                if (!target.openPopup()) return skipOnce(`nopopup:${key}`, `${label}: the target hotspot has no popup content; skipped.`);
                return DONE;
            case 'playAudio':
            case 'stopAudio':
            case 'toggleAudio': {
                const audio = target.audio;
                if (!audio) return skipOnce(`noaudio:${key}`, `${label}: the target hotspot has no audio; skipped.`);
                if (!audio.ready()) {
                    logger.warn(`${PREFIX} ${label}: the target's audio is still loading; skipped.`);
                    return DONE;
                }
                const state = audio.state();
                if (type === 'stopAudio') {
                    if (state !== 'stopped') audio.stop();
                    return DONE;
                }
                if (state === 'playing') {
                    if (type === 'toggleAudio') {
                        if (audio.clickBehavior === 'stop-restart') audio.stop();
                        else audio.pause();
                    }
                    return DONE;
                }
                if (state === 'paused' && audio.clickBehavior === 'pause-resume') audio.resume();
                else audio.play();
                return DONE;
            }
            default:
                return DONE;
        }
    };

    const runModelAction = (type: LinkedModelActionType, target: LinkedModelTarget, key: string, label: string): Promise<void> => {
        const animation = target.animation;
        if (!animation) return skipOnce(`noanim:${key}`, `${label}: the target model has no animations (or is still loading); skipped.`);
        const playing = animation.isPlaying();
        if (type === 'playAnimation' || (type === 'toggleAnimation' && !playing)) {
            if (!playing) animation.play();
        } else if (playing) {
            animation.stop();
        }
        return DONE;
    };

    const runEntityAnimationAction = (type: LinkedEntityAnimationActionType, target: LinkedEntityAnimationTarget, key: string, label: string): Promise<void> => {
        if (!target.timeBased) return skipOnce(`scroll:${key}`, `${label}: scroll-linked animations follow the tour and can't be played; skipped.`);
        const playing = target.isPlaying();
        switch (type) {
            case 'restartEntityAnimation':
                target.restart();
                break;
            case 'pauseEntityAnimation':
                if (playing) target.pause();
                break;
            case 'toggleEntityAnimation':
                if (playing) target.pause();
                else target.play();
                break;
            case 'playEntityAnimation':
            default:
                if (!playing) target.play();
                break;
        }
        return DONE;
    };

    const runAction = (input: LinkedActionInput, source?: LinkedActionSource): Promise<void> => {
        try {
            const action = normalizeLinkedAction(input);
            if (!action) return skipOnce(`invalid:${JSON.stringify(input ?? null)}`, `skipping a malformed linked action: ${JSON.stringify(input ?? null)}`);
            const { target } = action;
            const key = `${target.kind}:${target.id}:${action.action}`;
            const label = `${action.action} → ${target.kind} "${target.id}"`;
            if (source && source.kind === target.kind && source.id === target.id) {
                return skipOnce(`self:${key}`, `${label}: a source can't target itself; skipped.`);
            }
            if (target.kind === 'hotspot') {
                const hotspot = resolver.getHotspot(target.id);
                if (!hotspot) return skipOnce(`missing:${key}`, `${label}: no such hotspot (deleted?); skipped.`);
                return runHotspotAction(action.action as LinkedHotspotActionType, hotspot, key, label);
            }
            if (target.kind === 'animation') {
                const animation = resolver.getAnimation?.(target.id) ?? null;
                if (!animation) return skipOnce(`missing:${key}`, `${label}: no such animation (deleted or disabled?); skipped.`);
                return runEntityAnimationAction(action.action as LinkedEntityAnimationActionType, animation, key, label);
            }
            const model = resolver.getModel(target.id);
            if (!model) return skipOnce(`missing:${key}`, `${label}: no such 3D model (deleted?); skipped.`);
            return runModelAction(action.action as LinkedModelActionType, model, key, label);
        } catch (err) {
            // One broken action must not stop the source's other actions.
            logger.warn(`${PREFIX} linked action failed: ${errorText(err as ErrorLike)}`);
            return DONE;
        }
    };

    return {
        runAction,
        run(actions, source) {
            if (!Array.isArray(actions) || actions.length === 0) return DONE;
            const pending = actions.map((action) => runAction(action, source));
            return Promise.all(pending).then(() => undefined);
        },
    };
}

// ── Trigger hold (see "Trigger hold" above) ──────────────────────────────────────────────

/** Runtime entities carry the hold; undefined = no linked action is overriding the trigger. */
export interface LinkedTriggerHoldState {
    linkedTriggerHold?: boolean;
}

/**
 * Record that a linked action overrode the target's automatic media trigger while that trigger's
 * condition (in proximity range / in visible range) was `condition`. null = the target has no
 * automatic trigger (click mode), so nothing is held.
 */
export function holdAutoTrigger(holder: LinkedTriggerHoldState, condition: boolean | null): void {
    holder.linkedTriggerHold = condition === null ? undefined : condition;
}

/**
 * True while a hold applies (the condition is still what it was when the action ran): the
 * automatic trigger must not play or pause the media this frame. Releases the hold, and returns
 * false, as soon as the condition differs, so the trigger acts on its next real change.
 */
export function isAutoTriggerHeld(holder: LinkedTriggerHoldState, condition: boolean | null): boolean {
    const held = holder.linkedTriggerHold;
    if (held === undefined) return false;
    if (condition !== null && condition === held) return true;
    holder.linkedTriggerHold = undefined;
    return false;
}
